import { useState } from "react";
import { Bot, Search, Users, X } from "lucide-react";
import {
  useAdminConversationOwners,
  useAdminConversations,
} from "../api/aronAdminApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { fmtDateTime, fmtUsd } from "./AronAuditView";
import type { AronAdminConversation } from "../types";

/**
 * Zakładka „Wszystkie" na liście rozmów ARON-a — rozmowy WSZYSTKICH pracowników
 * (11E/C, wyłącznie CEO+DEV). Kliknięcie otwiera kartę podglądu w tym samym
 * warsztacie, obok własnych rozmów, zamiast odsyłać na osobną stronę audytu.
 *
 * Szukanie, filtr osoby i sortowanie robi SERWER: lista jest stronicowana, a
 * własnych wątków bywa kilkaset — filtrowanie tego, co akurat wisi na ekranie,
 * ukryłoby wątek kolegi, po który się tu przyszło.
 */
export function AronAdminThreadList({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (conv: AronAdminConversation) => void;
}) {
  const [term, setTerm] = useState("");
  const [owner, setOwner] = useState("");
  const query = useDebouncedValue(term.trim(), 300);
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useAdminConversations({
      search: query || undefined,
      user: owner || undefined,
      ordering: "activity",
      dir: "desc",
    });
  const { data: owners = [] } = useAdminConversationOwners();

  const rows = (data?.pages ?? []).flatMap((p) => p.rows);
  const total = data?.pages[0]?.count ?? 0;

  return (
    // Rodzic (lista rozmów) jest kolumną flex — panel bierze resztę wysokości,
    // a przewija się dopiero wewnętrzna lista.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-3 pb-2">
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Szukaj: tytuł, pracownik…"
            aria-label="Szukaj w cudzych rozmowach"
            className="w-full border border-gray-300 bg-white py-1.5 pl-7 pr-7 text-xs text-gray-700 placeholder:text-gray-400 focus:border-red-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
          />
          {term && (
            <button
              type="button"
              onClick={() => setTerm("")}
              title="Wyczyść"
              aria-label="Wyczyść wyszukiwanie"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className="relative mt-2">
          <Users
            size={13}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            aria-label="Pracownik"
            className="w-full border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-xs text-gray-700 focus:border-red-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
          >
            <option value="">Wszyscy pracownicy</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.conversation_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {isLoading ? (
          <div className="p-3 text-sm text-gray-400">Ładowanie…</div>
        ) : isError ? (
          // Pusty stan nie może kłamać: odmowa serwera (np. zdjęte uprawnienie
          // CEO+DEV) wyglądałaby jak „firma nie rozmawia z ARON-em".
          <div className="p-3 text-sm text-amber-700 dark:text-amber-400">
            Nie udało się wczytać rozmów firmy — brak uprawnień (wymagane: CEO
            oraz developer) albo błąd połączenia.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-3 text-sm text-gray-400">
            {query || owner
              ? "Brak rozmów pasujących do filtrów."
              : "Brak rozmów."}
          </div>
        ) : (
          <>
            <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Rozmowy firmy
              <span className="ml-auto float-right font-normal text-gray-300 dark:text-gray-600">
                {rows.length}
                {rows.length !== total ? ` z ${total}` : ""}
              </span>
            </div>
            {rows.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c)}
                title={`${c.title || "(bez tytułu)"} — ${c.user_name}`}
                className={`flex w-full items-start gap-2 border-l-[3px] px-2 py-2 text-left ${
                  selectedId === c.id
                    ? "border-red-600 bg-red-50 dark:bg-red-900/20"
                    : "border-transparent hover:bg-gray-200/60 dark:hover:bg-gray-800"
                }`}
              >
                <Bot size={14} className="mt-0.5 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-gray-700 dark:text-gray-200">
                    {c.title || "(bez tytułu)"}
                    {c.deleted_at && (
                      <span className="ml-1 bg-gray-200 px-1 text-[10px] text-gray-500 dark:bg-gray-700">
                        usunięta
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-[11px] text-gray-400">
                    {c.user_name}
                  </span>
                  <span className="block truncate text-[10px] text-gray-400">
                    {fmtDateTime(c.last_activity_at ?? c.last_message_at)} ·{" "}
                    {c.message_count} wiad. ·{" "}
                    {fmtUsd(c.total_cost_usd)} $
                  </span>
                </span>
              </button>
            ))}
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {isFetchingNextPage
                  ? "Ładowanie…"
                  : `Pokaż więcej (${total - rows.length})`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
